export const LOCAL_AUTHOR_STORAGE_KEY = "fishystuff.local-author.v1";

function result(kind, fields = {}) {
  return Object.freeze({ kind, ...fields });
}

export function createLocalAuthorProvider({
  storage = null,
  eventTarget = null,
} = {}) {
  let disposed = false;
  const invalidationListeners = new Set();

  function unavailable(operation, reason) {
    return result("unavailable", { operation, reason });
  }

  function readDocument() {
    try {
      if (!storage || typeof storage.getItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      return result("available", {
        raw: storage.getItem(LOCAL_AUTHOR_STORAGE_KEY),
      });
    } catch (_error) {
      return unavailable("read", "storage-read-failed");
    }
  }

  function writeDocument(raw) {
    if (typeof raw !== "string") {
      throw new TypeError("Local author document must be encoded text.");
    }
    try {
      if (
        !storage
        || typeof storage.setItem !== "function"
        || typeof storage.getItem !== "function"
      ) {
        throw new TypeError("Storage is unavailable.");
      }
      storage.setItem(LOCAL_AUTHOR_STORAGE_KEY, raw);
      if (storage.getItem(LOCAL_AUTHOR_STORAGE_KEY) !== raw) {
        return unavailable("write", "storage-write-not-acknowledged");
      }
      return result("acknowledged");
    } catch (_error) {
      return unavailable("write", "storage-write-failed");
    }
  }

  function removeDocument() {
    try {
      if (
        !storage
        || typeof storage.removeItem !== "function"
        || typeof storage.getItem !== "function"
      ) {
        throw new TypeError("Storage is unavailable.");
      }
      storage.removeItem(LOCAL_AUTHOR_STORAGE_KEY);
      if (storage.getItem(LOCAL_AUTHOR_STORAGE_KEY) !== null) {
        return unavailable("remove", "storage-remove-not-acknowledged");
      }
      return result("acknowledged");
    } catch (_error) {
      return unavailable("remove", "storage-remove-failed");
    }
  }

  function notifyInvalidation() {
    if (disposed) return;
    for (const listener of invalidationListeners) {
      try {
        listener();
      } catch (error) {
        globalThis.reportError?.(error);
      }
    }
  }

  function handleStorage(event) {
    if (
      event?.key === null
      || event?.key === LOCAL_AUTHOR_STORAGE_KEY
    ) {
      notifyInvalidation();
    }
  }

  function handlePageShow(event) {
    if (event?.persisted === true) notifyInvalidation();
  }

  eventTarget?.addEventListener?.("storage", handleStorage);
  eventTarget?.addEventListener?.("pageshow", handlePageShow);

  function subscribeInvalidation(listener) {
    if (typeof listener !== "function") {
      throw new TypeError(
        "Local author provider invalidation listener must be a function.",
      );
    }
    if (disposed) return () => {};
    invalidationListeners.add(listener);
    return () => invalidationListeners.delete(listener);
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    invalidationListeners.clear();
    eventTarget?.removeEventListener?.("storage", handleStorage);
    eventTarget?.removeEventListener?.("pageshow", handlePageShow);
  }

  return Object.freeze({
    dispose,
    readDocument,
    removeDocument,
    subscribeInvalidation,
    writeDocument,
  });
}

export function createBrowserLocalAuthorProvider() {
  let storage = null;
  try {
    storage = globalThis.localStorage ?? null;
  } catch (_error) {
    storage = null;
  }
  return createLocalAuthorProvider({
    storage,
    eventTarget: globalThis.window ?? null,
  });
}
